/**
 * /checkin/admin/signin — dedicated admin sign-in for the Check In
 * with Dr. C administration console.
 *
 * The customer-facing /checkin/signin page always routes to
 * /checkin/home; that meant Dr. C could not reach /checkin/admin
 * without manually re-navigating after a successful login. This page
 * uses the SAME /checkin/auth/login endpoint (which already issues an
 * admin token when the account carries role='checkin_admin') but:
 *
 *   · Rejects non-admin accounts with an "admin access only" message
 *     BEFORE persisting the customer session — so authenticated
 *     customer accounts never accidentally end up on /checkin/admin.
 *   · Routes successful admins to /checkin/admin (honouring ?next=).
 *   · Preserves the visible brand + palette used by the console.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import {
  CheckInShell, CheckInLogo, PrimaryButton, TextField, CHECKIN_PALETTE,
} from "../_shell";
import {
  checkinApi, setToken, setCachedUser, getToken, getCachedUser, clearAuth,
} from "@/lib/checkinApi";

function isAdminUser(u) {
  const role = u?.role || u?.admin_role || "";
  return role === "checkin_admin" || role === "super_admin";
}

export default function CheckInAdminSignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextParam = params.get("next") || "/checkin/admin";
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // If an admin token is already in localStorage, skip the form.
  useEffect(() => {
    const cached = getCachedUser();
    if (getToken() && isAdminUser(cached)) {
      navigate(nextParam.startsWith("/checkin/admin") ? nextParam : "/checkin/admin",
                { replace: true });
    }
  }, [navigate, nextParam]);

  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const out = await checkinApi.login({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      if (!isAdminUser(out.user)) {
        // Do NOT persist the customer session here — signing in as an
        // admin at this URL must fail cleanly rather than silently
        // dropping the person into the customer experience. The neutral
        // wording avoids revealing whether an email exists or which role
        // is missing.
        clearAuth();
        setErr("We could not sign you into the administrator console. Verify your credentials or contact the platform administrator.");
        return;
      }
      setToken(out.token);
      setCachedUser(out.user);
      const dest = nextParam && nextParam.startsWith("/checkin/admin")
        ? nextParam : "/checkin/admin";
      navigate(dest, { replace: true });
    } catch (e2) {
      // Neutral copy for both 401 (bad credentials) and 403 (wrong role
      // or account suspended). The detailed cause is available to
      // administrators via secured backend logs using the reference
      // code in the response detail.
      const ref = e2?.detail?.ref ? ` (Ref ${e2.detail.ref})` : "";
      setErr(`We could not sign you into the administrator console. Verify your credentials or contact the platform administrator.${ref}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <CheckInShell testid="checkin-admin-signin">
      <div className="pt-2 flex items-center justify-between">
        <CheckInLogo small />
        <Link to="/" className="text-xs" style={{ color: CHECKIN_PALETTE.purple }}
              data-testid="admin-signin-home">
          ← Return home
        </Link>
      </div>

      <div className="mt-8">
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest"
             style={{ background: CHECKIN_PALETTE.purple, color: "white" }}>
          <ShieldCheck className="w-3.5 h-3.5" /> Administrator
        </div>
        <h1 className="mt-3 font-serif text-3xl leading-tight"
            style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          Check In with Dr. C · Admin Console
        </h1>
        <p className="mt-3 text-[13.5px]" style={{ color: "#5C5468" }}>
          Sign in with the founding administrator account to open the console.
        </p>
      </div>

      <form className="mt-6 space-y-4" onSubmit={onSubmit} data-testid="admin-signin-form">
        <TextField
          label="Admin email" testid="admin-signin-email" required type="email"
          autoComplete="email" inputMode="email" value={form.email}
          onChange={(e) => up("email", e.target.value)}
          placeholder="Enter administrator email"
        />
        <TextField
          label="Password" testid="admin-signin-password" required
          type="password" autoComplete="current-password"
          value={form.password} onChange={(e) => up("password", e.target.value)}
          placeholder="Your admin password"
        />

        {err ? (
          <div className="rounded-2xl border px-4 py-3 text-sm"
               style={{ borderColor: CHECKIN_PALETTE.danger, color: CHECKIN_PALETTE.danger, background: "#FDF1F3" }}
               data-testid="admin-signin-error">
            {err}
          </div>
        ) : null}

        <div className="pt-2">
          <PrimaryButton type="submit" disabled={submitting} testid="admin-signin-submit">
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Signing you in…
              </span>
            ) : "Enter admin console"}
          </PrimaryButton>
        </div>

        <p className="text-center text-[12px] pt-2" style={{ color: "#6C6478" }}>
          Looking for the member sign-in?{" "}
          <Link to="/checkin/signin" className="underline"
                style={{ color: CHECKIN_PALETTE.purple }}
                data-testid="admin-signin-member-link">
            Member sign in
          </Link>
        </p>
      </form>
    </CheckInShell>
  );
}
